import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type { PreviewData } from "@/lib/types";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  PlusCircle,
  Eye,
  FolderKanban,
  Activity,
  ArrowRight,
  BarChart3,
  Clock,
  Users,
  Share2,
  Monitor,
  Smartphone,
  Tablet,
  Crown,
  Target,
} from "lucide-react";
import { PreviewCard } from "@/components/preview/preview-card";
import { BUCKET_NAME } from "@/lib/constants";
import {
  getDashboardStats,
  getMostUsedModels,
  getMostViewedPreviews,
  getRecentViewerActivity,
  getViewsOverTimeData,
  getGeographicDistribution,
  type DashboardStats,
  type MostUsedModel,
  type MostViewedPreview,
  type RecentViewerActivity,
} from "@/services/dashboard-service";
import ReactCountryFlag from "react-country-flag";
import {
  ViewsOverTimeChart,
  GeographicDistributionChart,
} from "@/components/charts/analytics-charts";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Dashboard() {
  const { t } = useTranslation("dashboard");
  const [recentPreviews, setRecentPreviews] = useState<PreviewData[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [mostUsedModels, setMostUsedModels] = useState<MostUsedModel[]>([]);
  const [mostViewedPreviews, setMostViewedPreviews] = useState<
    MostViewedPreview[]
  >([]);
  const [recentActivity, setRecentActivity] = useState<RecentViewerActivity[]>(
    []
  );
  const [viewsOverTimeData, setViewsOverTimeData] = useState<Array<{ date: string; views: number; uniqueVisitors: number }>>([]);
  const [geoDistribution, setGeoDistribution] = useState<Array<{ country: string; views: number; percentage: number }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingCharts, setIsLoadingCharts] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      fetchRecentPreviews(),
      fetchStats(),
      fetchMostUsedModels(),
      fetchMostViewedPreviews(),
      fetchRecentActivity(),
      fetchChartData(),
    ]);
  }, []);

  const fetchStats = async () => {
    try {
      setIsLoadingStats(true);
      const dashboardStats = await getDashboardStats();
      setStats(dashboardStats);
    } catch (err) {
      console.error("Error fetching dashboard stats:", err);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchRecentPreviews = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setIsLoading(true);
      const { data, error } = await supabase
        .from("previews")
        .select("*,model:available_models(name),project:projects(id,name)")
        .eq("user_id", user?.id)
        .order("created_at", { ascending: false })
        .limit(3);

      if (error) throw error;

      const previewsWithPublicUrls = data?.map((preview) => {
        if (preview.asset_path) {
          const {
            data: { publicUrl },
          } = supabase.storage
            .from(BUCKET_NAME)
            .getPublicUrl(preview.asset_path);

          return { ...preview, asset_path: publicUrl };
        }
        return preview;
      });

      setRecentPreviews(previewsWithPublicUrls || []);
    } catch (err) {
      console.error("Error fetching recent previews:", err);
      setError(t("errors.loadPreviews"));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchMostUsedModels = async () => {
    try {
      const models = await getMostUsedModels(3);
      setMostUsedModels(models);
    } catch (err) {
      console.error("Error fetching most used models:", err);
    }
  };

  const fetchMostViewedPreviews = async () => {
    try {
      const previews = await getMostViewedPreviews(3);
      setMostViewedPreviews(previews);
    } catch (err) {
      console.error("Error fetching most viewed previews:", err);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const activity = await getRecentViewerActivity(5);
      setRecentActivity(activity);
    } catch (err) {
      console.error("Error fetching recent activity:", err);
    }
  };

  const fetchChartData = async () => {
    try {
      setIsLoadingCharts(true);
      const [viewsData, geoData] = await Promise.all([
        getViewsOverTimeData(30),
        getGeographicDistribution(),
      ]);

      // Validate and format views over time data with better error handling
      const formattedViewsData = Array.isArray(viewsData)
        ? viewsData.map((item) => ({
            date: item.date || new Date().toISOString().split('T')[0],
            views: Number(item.views) || 0,
            uniqueVisitors: Number(item.unique_visitors || item.uniqueVisitors) || 0,
          }))
        : [];

      // Validate and format geographic data
      const formattedGeoData = Array.isArray(geoData)
        ? geoData.map((item) => ({
            country: item.country || "Unknown",
            views: Number(item.views) || 0,
            percentage: Number(item.percentage) || 0,
          }))
        : [];

      setViewsOverTimeData(formattedViewsData);
      setGeoDistribution(formattedGeoData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
      // Set empty arrays on error to prevent chart crashes
      setViewsOverTimeData([]);
      setGeoDistribution([]);
    } finally {
      // Add a minimum loading time to prevent flash
      setTimeout(() => {
        setIsLoadingCharts(false);
      }, 500);
    }
  };

  const handlePreviewDelete = (id: string) => {
    setRecentPreviews((prevPreviews) =>
      prevPreviews.filter((preview) => preview.id !== id)
    );
  };

  const handlePreviewEdit = (updatedPreview: PreviewData) => {
    setRecentPreviews((prevPreviews) =>
      prevPreviews.map((preview) =>
        preview.id === updatedPreview.id ? updatedPreview : preview
      )
    );
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffInMinutes = Math.floor(
      (now.getTime() - past.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return t("timeAgo.justNow");
    if (diffInMinutes < 60) return t("timeAgo.minutesAgo", { count: diffInMinutes });
    if (diffInMinutes < 1440) return t("timeAgo.hoursAgo", { count: Math.floor(diffInMinutes / 60) });
    return t("timeAgo.daysAgo", { count: Math.floor(diffInMinutes / 1440) });
  };

  const DeviceIcon = ({ deviceType }: { deviceType: string }) => {
    switch (deviceType?.toLowerCase()) {
      case "mobile":
        return <Smartphone className="h-3 w-3" />;
      case "tablet":
        return <Tablet className="h-3 w-3" />;
      default:
        return <Monitor className="h-3 w-3" />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 container mx-auto px-4 py-6 space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("page.title")}</h1>
            <p className="text-muted-foreground">
              {t("page.subtitle")}
            </p>
          </div>
          <Button asChild className="hidden sm:flex">
            <Link to="/models">
              <PlusCircle className="mr-2 h-4 w-4" />
              {t("buttons.createPreview")}
            </Link>
          </Button>
        </div>

        {/* Statistics Cards */}
        <div className="space-y-6">
          {/* Main KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title={t("stats.totalPreviews.title")}
              value={stats?.totalPreviews || 0}
              description={t("stats.totalPreviews.description")}
              icon={<Eye className="h-4 w-4" />}
              isLoading={isLoadingStats}
            />
            <StatCard
              title={t("stats.sharedPreviews.title")}
              value={stats?.sharablePreviews || 0}
              description={t("stats.sharedPreviews.description")}
              icon={<Share2 className="h-4 w-4" />}
              isLoading={isLoadingStats}
            />
            <StatCard
              title={t("stats.totalViews.title")}
              value={stats?.totalViews || 0}
              description={t("stats.totalViews.description")}
              icon={<BarChart3 className="h-4 w-4" />}
              isLoading={isLoadingStats}
            />
            <StatCard
              title={t("stats.avgViewTime.title")}
              value={stats?.averageViewTime || 0}
              description={t("stats.avgViewTime.description")}
              icon={<Clock className="h-4 w-4" />}
              isLoading={isLoadingStats}
              formatAsTime={true}
            />
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatCard
              title={t("stats.activeProjects.title")}
              value={stats?.activeProjects || 0}
              description={t("stats.activeProjects.description")}
              icon={<FolderKanban className="h-4 w-4" />}
              isLoading={isLoadingStats}
              subValue={
                stats?.archivedProjects
                  ? t("stats.activeProjects.archived", { count: stats.archivedProjects })
                  : undefined
              }
            />
            <StatCard
              title={t("stats.uniqueVisitors.title")}
              value={stats?.uniqueVisitors || 0}
              description={t("stats.uniqueVisitors.description")}
              icon={<Users className="h-4 w-4" />}
              isLoading={isLoadingStats}
            />
            <StatCard
              title={t("stats.lastActivity.title")}
              value={stats?.lastSeenPreview ? 1 : 0}
              description={t("stats.lastActivity.description")}
              icon={<Activity className="h-4 w-4" />}
              isLoading={isLoadingStats}
              highlight={true}
              subValue={
                stats?.lastSeenPreview
                  ? t("stats.lastActivity.lastSeen", { name: stats.lastSeenPreview.name })
                  : t("stats.lastActivity.noActivity")
              }
            />
          </div>
        </div>

        {/* Analytics Charts Section */}
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight">
                {t("analytics.title")}
              </h2>
              <p className="text-muted-foreground">
                {t("analytics.subtitle")}
              </p>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <GeographicDistributionChart
              data={geoDistribution}
              isLoading={isLoadingCharts}
            />
            <ViewsOverTimeChart
              data={viewsOverTimeData}
              isLoading={isLoadingCharts}
            />
          </div>
        </div>

        {/* Analytics Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Used Models */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                {t("sections.mostUsedModels.title")}
              </CardTitle>
              <CardDescription>
                {t("sections.mostUsedModels.subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mostUsedModels.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Target className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>{t("sections.mostUsedModels.noData")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mostUsedModels.map((model, index) => (
                    <div
                      key={model.model_uuid}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 transition-colors duration-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center transition-colors">
                          <span className="text-sm font-bold text-primary">
                            #{index + 1}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium transition-colors">
                            {model.model_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {t("sections.mostUsedModels.lastUsed", { time: formatTimeAgo(model.recent_usage) })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary">
                          {t("sections.mostUsedModels.uses", { count: model.usage_count })}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button variant="outline" asChild className="w-full">
                <Link to="/models">
                  {t("buttons.viewAllModels")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>

          {/* Most Viewed Previews */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5" />
                {t("sections.topPerformingPreviews.title")}
              </CardTitle>
              <CardDescription>
                {t("sections.topPerformingPreviews.subtitle")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mostViewedPreviews.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <Crown className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>{t("sections.topPerformingPreviews.noData")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mostViewedPreviews.map((preview, index) => (
                    <Link
                      key={preview.preview_id}
                      to={`/viewer/${preview.preview_id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors duration-200 group cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <span className="text-sm font-bold text-primary">
                            #{index + 1}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate group-hover:text-primary transition-colors">
                            {preview.preview_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {preview.model_name} •{" "}
                            {t("sections.topPerformingPreviews.avg", { 
                              duration: formatDuration(Math.round(preview.avg_duration))
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <Badge variant="secondary">
                            {t("sections.topPerformingPreviews.views", { count: preview.total_views })}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {t("sections.topPerformingPreviews.unique", { count: preview.unique_viewers })}
                          </p>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button variant="outline" asChild className="w-full">
                <Link to="/previews">
                  {t("buttons.viewAllPreviews")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardFooter>
          </Card>
        </div>

{/* Recent Viewer Activity */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-500/10 text-green-600">
                <Activity className="h-5 w-5" />
              </div>
              {t("sections.recentActivity.title")}
            </CardTitle>
            <CardDescription>
              {t("sections.recentActivity.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-0">
            {recentActivity.length === 0 ? (
              <div className="text-center py-12 px-6 text-muted-foreground">
                <div className="mx-auto w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                  <Activity className="h-8 w-8 opacity-50" />
                </div>
                <h3 className="font-medium text-foreground mb-2">{t("sections.recentActivity.noData")}</h3>
                <p className="text-sm max-w-sm mx-auto">
                  {t("sections.recentActivity.noDataSubtitle")}
                </p>
              </div>
            ) : (
              <div className="space-y-0">
                {recentActivity.map((activity, index) => (
                  <div
                    key={index}
                    className="group relative flex items-center justify-between p-4 px-6 hover:bg-muted/30 transition-colors duration-200 border-b border-border/50 last:border-b-0"
                  >
                    {/* Left side - Activity info */}
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      {/* Country and Device indicators */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="relative">
                          {activity.viewer_country ? (
                            <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-background shadow-sm">
                              <ReactCountryFlag
                                countryCode={activity.viewer_country.toUpperCase()}
                                svg
                                style={{ 
                                  width: "100%", 
                                  height: "100%",
                                  objectFit: "cover"
                                }}
                              />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <span className="text-xs font-medium text-muted-foreground">?</span>
                            </div>
                          )}
                          {/* Device indicator badge */}
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-background border border-border flex items-center justify-center">
                            <DeviceIcon deviceType={activity.viewer_device} />
                          </div>
                        </div>
                      </div>

                      {/* Preview and location info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                            {activity.preview_name}
                          </p>
                          <div className="w-1 h-1 rounded-full bg-muted-foreground/40 flex-shrink-0" />
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {formatTimeAgo(activity.viewed_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span className="capitalize">
                            {activity.viewer_country || t("sections.recentActivity.unknownLocation")}
                          </span>
                          <span>•</span>
                          <span className="capitalize">
                            {activity.viewer_device || t("sections.recentActivity.unknownDevice")}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right side - Duration and metrics */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <Badge 
                          variant="secondary" 
                          className="font-mono text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 transition-colors"
                        >
                          {formatDuration(activity.view_duration)}
                        </Badge>
                      </div>
                      
                      {/* Engagement indicator */}
                      <div className="w-2 h-8 rounded-full bg-gradient-to-t from-green-500/20 to-green-500/60 relative overflow-hidden">
                        <div 
                          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-green-500 to-green-400 rounded-full transition-all duration-300"
                          style={{ 
                            height: `${Math.min(100, (activity.view_duration / 300) * 100)}%` 
                          }}
                        />
                      </div>
                    </div>

                    {/* Hover effect overlay */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          
          {/* Footer with summary stats */}
          {recentActivity.length > 0 && (
            <div className="border-t bg-muted/30 px-6 py-3">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{t("sections.recentActivity.showing", { count: recentActivity.length })}</span>
                <span>
                  {t("sections.recentActivity.avgSession", { 
                    duration: formatDuration(
                      Math.round(
                        recentActivity.reduce((acc, activity) => acc + activity.view_duration, 0) / recentActivity.length
                      )
                    )
                  })}
                </span>
              </div>
            </div>
          )}
        </Card>

        {/* Quick Actions */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold tracking-tight">
              {t("sections.quickActions.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ActionCard
              title={t("sections.quickActions.createPreview.title")}
              description={t("sections.quickActions.createPreview.description")}
              icon={<PlusCircle className="h-5 w-5" />}
              href="/models"
              buttonText={t("buttons.getStarted")}
              variant="default"
            />
            <ActionCard
              title={t("sections.quickActions.manageProjects.title")}
              description={t("sections.quickActions.manageProjects.description")}
              icon={<FolderKanban className="h-5 w-5" />}
              href="/projects"
              buttonText={t("buttons.viewProjects")}
              variant="outline"
            />
            <ActionCard
              title={t("sections.quickActions.browseModels.title")}
              description={t("sections.quickActions.browseModels.description")}
              icon={<BarChart3 className="h-5 w-5" />}
              href="/models"
              buttonText={t("buttons.browseModels")}
              variant="outline"
            />
          </div>
        </div>

        {/* Recent Previews */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight">
                {t("sections.recentPreviews.title")}
              </h2>
              <p className="text-muted-foreground">
                {t("sections.recentPreviews.subtitle")}
              </p>
            </div>
            <Button variant="outline" asChild size="sm">
              <Link to="/previews" className="flex items-center">
                {t("buttons.viewAll")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array(3)
                .fill(0)
                .map((_, i) => (
                  <Card key={i} className="overflow-hidden">
                    <div className="aspect-video">
                      <Skeleton className="h-full w-full" />
                    </div>
                    <CardContent className="p-4 space-y-2">
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-3 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}

          {error && (
            <Card className="p-6 text-center">
              <p className="text-destructive">{error}</p>
            </Card>
          )}

          {!isLoading && !error && recentPreviews.length === 0 && (
            <Card className="p-8 text-center bg-muted/50">
              <div className="space-y-4">
                <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <PlusCircle className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{t("sections.recentPreviews.noData")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("sections.recentPreviews.noDataSubtitle")}
                  </p>
                  <Button asChild>
                    <Link to="/models">
                      <PlusCircle className="mr-2 h-4 w-4" />
                      {t("sections.recentPreviews.createFirst")}
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {!isLoading && !error && recentPreviews.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recentPreviews.map((preview) => (
                <PreviewCard
                  key={preview.id}
                  preview={preview}
                  onDelete={handlePreviewDelete}
                  onEdit={handlePreviewEdit}
                  showProjectTag={true}
                />
              ))}
            </div>
          )}
        </div>

        {/* Mobile Quick Action Button */}
        <div className="sm:hidden fixed bottom-6 right-6">
          <Button
            asChild
            size="icon"
            className="rounded-full shadow-lg h-10 w-10"
          >
            <Link to="/models">
              <PlusCircle />
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: number;
  description: string;
  icon: React.ReactNode;
  isLoading: boolean;
  trend?: string;
  percentage?: number;
  subValue?: string;
  highlight?: boolean;
  formatAsTime?: boolean;
}

function StatCard({
  title,
  value,
  description,
  icon,
  isLoading,
  trend,
  percentage,
  subValue,
  highlight,
  formatAsTime,
}: StatCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            <Skeleton className="h-4 w-20" />
          </CardTitle>
          <Skeleton className="h-4 w-4" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-7 w-16 mb-1" />
          <Skeleton className="h-3 w-24" />
        </CardContent>
      </Card>
    );
  }

  const formatValue = (val: number) => {
    if (formatAsTime) {
      if (val < 60) return `${val}s`;
      const minutes = Math.floor(val / 60);
      const seconds = val % 60;
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }
    return val.toLocaleString();
  };

  return (
    <Card className={highlight ? "border-primary/50 bg-primary/5" : ""}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className={highlight ? "text-primary" : "text-muted-foreground"}>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{formatValue(value)}</div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">{description}</p>
          {trend && (
            <p className="text-xs text-green-600 flex items-center">+{trend}</p>
          )}
          {percentage !== undefined && percentage > 0 && (
            <p className="text-xs text-muted-foreground">
              {percentage}% of total
            </p>
          )}
          {subValue && (
            <p className="text-xs text-muted-foreground">{subValue}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Action Card Component
interface ActionCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: string;
  buttonText: string;
  variant?: "default" | "outline";
}

function ActionCard({
  title,
  description,
  icon,
  href,
  buttonText,
  variant = "default",
}: ActionCardProps) {
  return (
    <Card className="group hover:shadow-md transition-shadow duration-200">
      <CardHeader>
        <div className="flex items-center space-x-2">
          <div className="p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
            {icon}
          </div>
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
        <CardDescription className="mt-2">{description}</CardDescription>
      </CardHeader>
      <CardFooter>
        <Button asChild variant={variant} className="w-full">
          <Link to={href} className="flex items-center justify-center">
            {buttonText}
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
